import { useEffect } from "react";
import { SEO_SITE_URL, IS_PREVIEW } from "../lib/siteConfig";

const SITE = "Tiff. Wellness Co.";

function upsertMeta(attr, key, content) {
  let m = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!m) {
    m = document.createElement("meta");
    m.setAttribute(attr, key);
    document.head.appendChild(m);
  }
  m.content = content;
}

export default function usePageMeta(title, description, opts = {}) {
  const { robots = "index,follow", jsonLd = null, ogImage = "/images/tiff-wellness-logo-original.jpg", schemaType } = opts;
  useEffect(() => {
    if (title) {
      document.title = title;
      upsertMeta("property", "og:title", title);
      upsertMeta("name", "twitter:title", title);
    }
    if (description) {
      upsertMeta("name", "description", description);
      upsertMeta("property", "og:description", description);
      upsertMeta("name", "twitter:description", description);
    }
    const effectiveRobots = IS_PREVIEW && !robots.includes("noindex") ? "noindex,follow" : robots;
    upsertMeta("name", "robots", effectiveRobots);
    upsertMeta("property", "og:type", "website");
    upsertMeta("property", "og:site_name", SITE);
    upsertMeta("name", "twitter:card", "summary");
    const origin = SEO_SITE_URL; // SEO artifacts point at the planned production domain
    const canonicalUrl = `${origin}${window.location.pathname}`;
    upsertMeta("property", "og:url", canonicalUrl);
    upsertMeta("property", "og:image", `${origin}${ogImage}`);
    let link = document.head.querySelector('link[rel="canonical"]');
    if (!link) {
      link = document.createElement("link");
      link.rel = "canonical";
      document.head.appendChild(link);
    }
    link.href = canonicalUrl;

    document.head.querySelectorAll('script[data-page-jsonld="1"]').forEach((s) => s.remove());
    const noindex = robots.includes("noindex");
    const pageName = (title || "").split("|")[0].trim();
    const blocks = [];
    const webPage = { "@context": "https://schema.org", "@type": schemaType || "WebPage", name: pageName, url: canonicalUrl };
    if (!noindex) {
      webPage.description = description;
      webPage.isPartOf = { "@type": "WebSite", name: SITE, url: `${origin}/` };
    }
    blocks.push(webPage); // private routes get minimal WebPage only — never PII or transactional data
    const extra = jsonLd ? (Array.isArray(jsonLd) ? jsonLd : [jsonLd]) : [];
    const hasBreadcrumb = extra.some((o) => o["@type"] === "BreadcrumbList");
    if (!noindex && window.location.pathname !== "/" && !hasBreadcrumb) {
      blocks.push({
        "@context": "https://schema.org", "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home", item: `${origin}/` },
          { "@type": "ListItem", position: 2, name: pageName, item: canonicalUrl },
        ],
      });
    }
    blocks.push(...extra);
    blocks.forEach((obj) => {
      const s = document.createElement("script");
      s.type = "application/ld+json";
      s.dataset.pageJsonld = "1";
      s.text = JSON.stringify(obj);
      document.head.appendChild(s);
    });
  }, [title, description, robots, ogImage, schemaType, JSON.stringify(jsonLd)]); // eslint-disable-line
}
